/**
 * Assets Page
 * Asset Discovery and Inventory Management (Issue #128)
 *
 * Displays discovered infrastructure assets from vulnerability scans
 * with filtering, search, and detail views.
 */

import { useState, useEffect, useCallback } from 'react'
import { api, Asset } from '../lib/api'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import {
  Server,
  Monitor,
  Network,
  Cloud,
  Database,
  Box,
  Cpu,
  HardDrive,
  Search,
  Plus,
  Trash2,
  Edit2,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  Tag,
  Shield,
  Eye,
  Loader2,
  Calendar,
  Activity,
  Wifi,
} from 'lucide-react'

type AssetStats = {
  total: number
  by_type: Record<string, number>
  by_status: Record<string, number>
  by_criticality: Record<string, number>
  by_environment: Record<string, number>
  stale_count: number
  with_vulnerabilities: number
}

type AssetVulnerability = {
  id: string
  vulnerability_id: string
  vulnerability_name: string
  severity: string
  cvss_score: number | null
  host: string
  port: number | null
  service: string | null
  description: string | null
  first_found: number
  last_found: number
  times_found: number
  is_resolved: number
}

type ScanHistory = {
  id: string
  scan_result_id: string
  ports_snapshot: Array<{ port: number; protocol: string; service: string }>
  os_snapshot: string | null
  new_ports: Array<{ port: number; protocol: string; service: string }>
  closed_ports: Array<{ port: number; protocol: string; service: string }>
  scanned_at: number
  scan_status: string
}

type Pagination = {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

const assetTypeIcons: Record<string, React.ReactNode> = {
  server: <Server className="h-5 w-5" />,
  workstation: <Monitor className="h-5 w-5" />,
  network_device: <Network className="h-5 w-5" />,
  cloud_instance: <Cloud className="h-5 w-5" />,
  container: <Box className="h-5 w-5" />,
  database: <Database className="h-5 w-5" />,
  application: <Cpu className="h-5 w-5" />,
  iot_device: <Wifi className="h-5 w-5" />,
  unknown: <HardDrive className="h-5 w-5" />,
}

const assetTypeLabels: Record<string, string> = {
  server: 'Server',
  workstation: 'Workstation',
  network_device: 'Network Device',
  cloud_instance: 'Cloud Instance',
  container: 'Container',
  database: 'Database',
  application: 'Application',
  iot_device: 'IoT Device',
  unknown: 'Unknown',
}

const criticalityColors: Record<string, string> = {
  critical: 'text-red-700 bg-red-100',
  high: 'text-orange-700 bg-orange-100',
  medium: 'text-yellow-700 bg-yellow-100',
  low: 'text-green-700 bg-green-100',
}

const statusColors: Record<string, string> = {
  active: 'text-green-700 bg-green-100',
  inactive: 'text-gray-700 bg-gray-100',
  decommissioned: 'text-red-700 bg-red-100',
  unknown: 'text-yellow-700 bg-yellow-100',
}

const severityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-blue-100 text-blue-800',
  info: 'bg-gray-100 text-gray-800',
}

export default function Assets() {
  const toast = useToast()
  const confirm = useConfirm()

  // State
  const [assets, setAssets] = useState<Asset[]>([])
  const [stats, setStats] = useState<AssetStats | null>(null)
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    limit: 25,
    offset: 0,
    hasMore: false,
  })
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(true)

  // Filters
  const [search, setSearch] = useState('')
  const [assetTypeFilter, setAssetTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [criticalityFilter, setCriticalityFilter] = useState('')
  const [environmentFilter, setEnvironmentFilter] = useState('')
  const [hasVulnsFilter, setHasVulnsFilter] = useState('')

  // Detail modal
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)
  const [assetVulnerabilities, setAssetVulnerabilities] = useState<AssetVulnerability[]>([])
  const [scanHistory, setScanHistory] = useState<ScanHistory[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailTab, setDetailTab] = useState<'overview' | 'vulnerabilities' | 'history'>('overview')

  // Create/Edit modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null)
  const [formData, setFormData] = useState({
    hostname: '',
    ip_address: '',
    asset_type: 'unknown',
    os: '',
    environment: '',
    criticality: 'medium',
    business_unit: '',
    notes: '',
    status: 'active',
    tags: [] as string[],
  })
  const [tagInput, setTagInput] = useState('')
  const [saving, setSaving] = useState(false)

  // Fetch assets
  const fetchAssets = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.getAssets({
        search: search || undefined,
        asset_type: assetTypeFilter || undefined,
        status: statusFilter || undefined,
        criticality: criticalityFilter || undefined,
        environment: environmentFilter || undefined,
        has_vulnerabilities: hasVulnsFilter as 'true' | 'false' | undefined,
        limit: pagination.limit,
        offset: pagination.offset,
      })
      if (response.success) {
        setAssets(response.assets)
        setPagination(response.pagination)
      }
    } catch (error) {
      console.error('Failed to fetch assets:', error)
      toast.error('Failed to fetch assets')
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, assetTypeFilter, statusFilter, criticalityFilter, environmentFilter, hasVulnsFilter, pagination.limit, pagination.offset])

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const response = await api.getAssetStats()
      if (response.success) {
        setStats(response.stats)
      }
    } catch (error) {
      console.error('Failed to fetch asset stats:', error)
    } finally {
      setStatsLoading(false)
    }
  }, [])

  // Load data on mount and filter changes
  useEffect(() => {
    fetchAssets()
  }, [fetchAssets])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  // Fetch asset details
  const fetchAssetDetails = async (asset: Asset) => {
    setSelectedAsset(asset)
    setDetailLoading(true)
    setDetailTab('overview')
    try {
      const response = await api.getAsset(asset.id)
      if (response.success) {
        setSelectedAsset(response.asset)
        setAssetVulnerabilities(response.vulnerabilities)
        setScanHistory(response.scan_history)
      }
    } catch (error) {
      console.error('Failed to fetch asset details:', error)
      toast.error('Failed to fetch asset details')
    } finally {
      setDetailLoading(false)
    }
  }

  // Handle create/edit
  const handleSave = async () => {
    if (!formData.hostname && !formData.ip_address) {
      toast.error('Either hostname or IP address is required')
      return
    }

    setSaving(true)
    try {
      if (editingAsset) {
        const response = await api.updateAsset(editingAsset.id, formData)
        if (response.success) {
          toast.success('Asset updated successfully')
          setShowCreateModal(false)
          setEditingAsset(null)
          fetchAssets()
          fetchStats()
        }
      } else {
        const response = await api.createAsset(formData)
        if (response.success) {
          toast.success('Asset created successfully')
          setShowCreateModal(false)
          fetchAssets()
          fetchStats()
        }
      }
    } catch (error: any) {
      console.error('Failed to save asset:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to save asset')
    } finally {
      setSaving(false)
    }
  }

  // Handle delete
  const handleDelete = async (asset: Asset) => {
    const confirmed = await confirm({
      title: 'Delete Asset',
      message: `Are you sure you want to delete ${asset.hostname || asset.ip_address}? This action cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger',
    })

    if (!confirmed) return

    try {
      const response = await api.deleteAsset(asset.id)
      if (response.success) {
        toast.success('Asset deleted successfully')
        setSelectedAsset(null)
        fetchAssets()
        fetchStats()
      }
    } catch (error: any) {
      console.error('Failed to delete asset:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to delete asset')
    }
  }

  // Handle tag add
  const handleAddTag = () => {
    const tag = tagInput.trim()
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }))
      setTagInput('')
    }
  }

  // Handle tag remove
  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))
  }

  // Open edit modal
  const openEditModal = (asset: Asset) => {
    setEditingAsset(asset)
    setFormData({
      hostname: asset.hostname || '',
      ip_address: asset.ip_address || '',
      asset_type: asset.asset_type,
      os: asset.os || '',
      environment: asset.environment || '',
      criticality: asset.criticality,
      business_unit: asset.business_unit || '',
      notes: asset.notes || '',
      status: asset.status,
      tags: asset.tags || [],
    })
    setShowCreateModal(true)
  }

  // Open create modal
  const openCreateModal = () => {
    setEditingAsset(null)
    setFormData({
      hostname: '',
      ip_address: '',
      asset_type: 'unknown',
      os: '',
      environment: '',
      criticality: 'medium',
      business_unit: '',
      notes: '',
      status: 'active',
      tags: [],
    })
    setShowCreateModal(true)
  }

  // Pagination handlers
  const goToPage = (page: number) => {
    const newOffset = page * pagination.limit
    setPagination(prev => ({ ...prev, offset: newOffset }))
  }

  const currentPage = Math.floor(pagination.offset / pagination.limit)
  const totalPages = Math.ceil(pagination.total / pagination.limit)

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Format relative time
  const formatRelativeTime = (timestamp: number) => {
    const now = Date.now()
    const diff = now - timestamp
    const days = Math.floor(diff / (24 * 60 * 60 * 1000))
    if (days === 0) return 'Today'
    if (days === 1) return 'Yesterday'
    if (days < 7) return `${days} days ago`
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`
    if (days < 365) return `${Math.floor(days / 30)} months ago`
    return `${Math.floor(days / 365)} years ago`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Asset Inventory</h1>
          <p className="text-gray-500">
            Discovered infrastructure assets from vulnerability scans
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { fetchAssets(); fetchStats(); }}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            Add Asset
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-lg border p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-16" />
            </div>
          ))}
        </div>
      ) : stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Server className="h-4 w-4" />
              Total Assets
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Shield className="h-4 w-4" />
              With Vulnerabilities
            </div>
            <div className="text-2xl font-bold text-orange-600">{stats.with_vulnerabilities}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <AlertTriangle className="h-4 w-4" />
              Critical Assets
            </div>
            <div className="text-2xl font-bold text-red-600">{stats.by_criticality.critical || 0}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Clock className="h-4 w-4" />
              Stale (30+ days)
            </div>
            <div className="text-2xl font-bold text-yellow-600">{stats.stale_count}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by hostname or IP..."
                value={search}
                onChange={e => {
                  setSearch(e.target.value)
                  setPagination(prev => ({ ...prev, offset: 0 }))
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <select
            value={assetTypeFilter}
            onChange={e => {
              setAssetTypeFilter(e.target.value)
              setPagination(prev => ({ ...prev, offset: 0 }))
            }}
            className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Types</option>
            {Object.entries(assetTypeLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value)
              setPagination(prev => ({ ...prev, offset: 0 }))
            }}
            className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="decommissioned">Decommissioned</option>
            <option value="unknown">Unknown</option>
          </select>
          <select
            value={criticalityFilter}
            onChange={e => {
              setCriticalityFilter(e.target.value)
              setPagination(prev => ({ ...prev, offset: 0 }))
            }}
            className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Criticality</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={environmentFilter}
            onChange={e => {
              setEnvironmentFilter(e.target.value)
              setPagination(prev => ({ ...prev, offset: 0 }))
            }}
            className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Environments</option>
            <option value="production">Production</option>
            <option value="staging">Staging</option>
            <option value="development">Development</option>
            <option value="test">Test</option>
            <option value="unknown">Unknown</option>
          </select>
          <select
            value={hasVulnsFilter}
            onChange={e => {
              setHasVulnsFilter(e.target.value)
              setPagination(prev => ({ ...prev, offset: 0 }))
            }}
            className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Any Vulnerabilities</option>
            <option value="true">Has Vulnerabilities</option>
            <option value="false">No Vulnerabilities</option>
          </select>
        </div>
      </div>

      {/* Assets Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : assets.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Server className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="font-medium">No assets found</p>
            <p className="text-sm mt-1">
              {search || assetTypeFilter || statusFilter || criticalityFilter
                ? 'Try adjusting your filters'
                : 'Assets will be automatically discovered during vulnerability scans'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Asset
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criticality
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vulnerabilities
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Seen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assets.map(asset => (
                  <tr key={asset.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 text-gray-500">
                          {assetTypeIcons[asset.asset_type] || assetTypeIcons.unknown}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {asset.hostname || asset.ip_address || 'Unknown'}
                          </div>
                          {asset.hostname && asset.ip_address && (
                            <div className="text-sm text-gray-500">{asset.ip_address}</div>
                          )}
                          {asset.os && (
                            <div className="text-xs text-gray-400">{asset.os}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {assetTypeLabels[asset.asset_type] || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${criticalityColors[asset.criticality]}`}>
                        {asset.criticality.charAt(0).toUpperCase() + asset.criticality.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[asset.status]}`}>
                        {asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(asset.open_vulnerability_count || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 text-red-600">
                          <AlertCircle className="h-4 w-4" />
                          {asset.open_vulnerability_count}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          None
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatRelativeTime(asset.last_seen)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => fetchAssetDetails(asset)}
                          className="p-1 text-gray-500 hover:text-primary-600"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(asset)}
                          className="p-1 text-gray-500 hover:text-primary-600"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(asset)}
                          className="p-1 text-gray-500 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} assets
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 0}
                className="p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={!pagination.hasMore}
                className="p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Asset Detail Modal */}
      {selectedAsset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-gray-500">
                  {assetTypeIcons[selectedAsset.asset_type] || assetTypeIcons.unknown}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedAsset.hostname || selectedAsset.ip_address || 'Unknown Asset'}
                  </h2>
                  {selectedAsset.hostname && selectedAsset.ip_address && (
                    <p className="text-sm text-gray-500">{selectedAsset.ip_address}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedAsset(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="px-6 border-b">
              <nav className="flex gap-4">
                {(['overview', 'vulnerabilities', 'history'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setDetailTab(tab)}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      detailTab === tab
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab === 'overview' && 'Overview'}
                    {tab === 'vulnerabilities' && `Vulnerabilities (${assetVulnerabilities.length})`}
                    {tab === 'history' && `Scan History (${scanHistory.length})`}
                  </button>
                ))}
              </nav>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : (
                <>
                  {/* Overview Tab */}
                  {detailTab === 'overview' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                          <div className="text-sm text-gray-900">
                            {assetTypeLabels[selectedAsset.asset_type] || 'Unknown'}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[selectedAsset.status]}`}>
                            {selectedAsset.status.charAt(0).toUpperCase() + selectedAsset.status.slice(1)}
                          </span>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Criticality</label>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${criticalityColors[selectedAsset.criticality]}`}>
                            {selectedAsset.criticality.charAt(0).toUpperCase() + selectedAsset.criticality.slice(1)}
                          </span>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Environment</label>
                          <div className="text-sm text-gray-900">
                            {selectedAsset.environment ? selectedAsset.environment.charAt(0).toUpperCase() + selectedAsset.environment.slice(1) : '-'}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Operating System</label>
                          <div className="text-sm text-gray-900">{selectedAsset.os || '-'}</div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">First Seen</label>
                          <div className="text-sm text-gray-900">{formatDate(selectedAsset.first_seen)}</div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Last Seen</label>
                          <div className="text-sm text-gray-900">{formatDate(selectedAsset.last_seen)}</div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Scan Count</label>
                          <div className="text-sm text-gray-900">{selectedAsset.scan_count}</div>
                        </div>
                      </div>

                      {/* Open Ports */}
                      {selectedAsset.ports && selectedAsset.ports.length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-2">Open Ports</label>
                          <div className="flex flex-wrap gap-2">
                            {selectedAsset.ports.map((port, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2.5 py-1 rounded bg-gray-100 text-sm"
                              >
                                {port.port}/{port.protocol}
                                {port.service && <span className="ml-1 text-gray-500">({port.service})</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tags */}
                      {selectedAsset.tags && selectedAsset.tags.length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-2">Tags</label>
                          <div className="flex flex-wrap gap-2">
                            {selectedAsset.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2.5 py-1 rounded bg-primary-100 text-primary-700 text-sm"
                              >
                                <Tag className="h-3 w-3 mr-1" />
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {selectedAsset.notes && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
                          <div className="text-sm text-gray-900 bg-gray-50 rounded-lg p-3">
                            {selectedAsset.notes}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Vulnerabilities Tab */}
                  {detailTab === 'vulnerabilities' && (
                    <div className="space-y-4">
                      {assetVulnerabilities.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                          <p className="font-medium">No vulnerabilities found</p>
                          <p className="text-sm mt-1">This asset has no known vulnerabilities</p>
                        </div>
                      ) : (
                        assetVulnerabilities.map(vuln => (
                          <div
                            key={vuln.id}
                            className={`p-4 rounded-lg border-l-4 ${
                              vuln.is_resolved ? 'bg-gray-50 border-gray-300' : 'bg-white border-red-500'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${severityColors[vuln.severity]}`}>
                                    {vuln.severity.toUpperCase()}
                                  </span>
                                  <span className="font-medium text-gray-900">{vuln.vulnerability_name}</span>
                                </div>
                                {vuln.description && (
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{vuln.description}</p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                  {vuln.port != null && <span>Port: {vuln.port}</span>}
                                  {vuln.service && <span>Service: {vuln.service}</span>}
                                  {vuln.cvss_score != null && <span>CVSS: {vuln.cvss_score}</span>}
                                  <span>Found {vuln.times_found}x</span>
                                  <span>Last: {formatRelativeTime(vuln.last_found)}</span>
                                </div>
                              </div>
                              {vuln.is_resolved ? (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Resolved</span>
                              ) : null}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* History Tab */}
                  {detailTab === 'history' && (
                    <div className="space-y-4">
                      {scanHistory.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Activity className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                          <p className="font-medium">No scan history</p>
                        </div>
                      ) : (
                        scanHistory.map(entry => (
                          <div key={entry.id} className="p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">
                                  {formatDate(entry.scanned_at)}
                                </span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${
                                entry.scan_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                              }`}>
                                {entry.scan_status}
                              </span>
                            </div>
                            {entry.os_snapshot && (
                              <p className="text-sm text-gray-600 mb-2">OS: {entry.os_snapshot}</p>
                            )}
                            {entry.new_ports?.length > 0 && (
                              <div className="flex items-center gap-2 text-sm text-green-600">
                                <span>New ports:</span>
                                {entry.new_ports.map((p, i) => (
                                  <span key={i} className="bg-green-100 px-1.5 py-0.5 rounded">
                                    {p.port}/{p.protocol}
                                  </span>
                                ))}
                              </div>
                            )}
                            {entry.closed_ports?.length > 0 && (
                              <div className="flex items-center gap-2 text-sm text-red-600 mt-1">
                                <span>Closed ports:</span>
                                {entry.closed_ports.map((p, i) => (
                                  <span key={i} className="bg-red-100 px-1.5 py-0.5 rounded">
                                    {p.port}/{p.protocol}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <button
                onClick={() => handleDelete(selectedAsset)}
                className="inline-flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="h-4 w-4" />
                Delete Asset
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedAsset(null)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => openEditModal(selectedAsset)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit Asset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingAsset ? 'Edit Asset' : 'Add Asset'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setEditingAsset(null)
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hostname</label>
                  <input
                    type="text"
                    value={formData.hostname}
                    onChange={e => setFormData(prev => ({ ...prev, hostname: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="server.example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
                  <input
                    type="text"
                    value={formData.ip_address}
                    onChange={e => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="192.168.1.1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Asset Type</label>
                  <select
                    value={formData.asset_type}
                    onChange={e => setFormData(prev => ({ ...prev, asset_type: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {Object.entries(assetTypeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="decommissioned">Decommissioned</option>
                    <option value="unknown">Unknown</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Criticality</label>
                  <select
                    value={formData.criticality}
                    onChange={e => setFormData(prev => ({ ...prev, criticality: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
                  <select
                    value={formData.environment}
                    onChange={e => setFormData(prev => ({ ...prev, environment: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select...</option>
                    <option value="production">Production</option>
                    <option value="staging">Staging</option>
                    <option value="development">Development</option>
                    <option value="test">Test</option>
                    <option value="unknown">Unknown</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operating System</label>
                <input
                  type="text"
                  value={formData.os}
                  onChange={e => setFormData(prev => ({ ...prev, os: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., Ubuntu 22.04, Windows Server 2019"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Unit</label>
                <input
                  type="text"
                  value={formData.business_unit}
                  onChange={e => setFormData(prev => ({ ...prev, business_unit: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., Engineering, Finance, IT"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Add a tag..."
                  />
                  <button
                    onClick={handleAddTag}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Add
                  </button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-primary-100 text-primary-700 text-sm"
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:text-primary-900"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Additional notes about this asset..."
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setEditingAsset(null)
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingAsset ? 'Save Changes' : 'Create Asset'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
